import { AbstractClient, CompositionClient } from "@/app/client";
import {
  WhatsAppInstanceResponse,
  WhatsAppInstanceCreate,
  WhatsAppApiResponse,
  QRCode,
  WhatsAppChatResponse,
  WhatsAppChatList,
  WhatsAppMessageList,
  PageOptions,
  WhatsAppMessageResponse,
} from "@/app/types";
import type {
  WhatsAppChatManagement,
  WhatsAppChatManagementUpdate,
  WhatsAppChatManagementWithShares,
} from "@/types/whatsapp-management";
import { UUID } from "crypto";

export default class APIWhatsAppSub extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  getInstances = async (): Promise<WhatsAppInstanceResponse[]> => {
    let res = await this._fetch({
      url: `/whatsapp/instances/`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  createInstance = async (
    body: WhatsAppInstanceCreate,
  ): Promise<WhatsAppInstanceResponse> => {
    let res = await this._fetch({
      url: `/whatsapp/instances/`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  deleteInstance = async (
    instanceId: string,
    deletionReason?: string,
  ): Promise<WhatsAppApiResponse> => {
    const params = deletionReason
      ? new URLSearchParams({ deletion_reason: deletionReason })
      : undefined;
    let res = await this._fetch({
      url: `/whatsapp/instances/${instanceId}${params ? `?${params}` : ""}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  connectInstance = async (instanceId: string): Promise<QRCode> => {
    let res = await this._fetch({
      url: `/whatsapp/instances/${instanceId}/connect`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  refreshInstance = async (
    instanceId: string,
  ): Promise<WhatsAppInstanceResponse> => {
    let res = await this._fetch({
      url: `/whatsapp/instances/${instanceId}/refresh`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  batchFetchChats = async (
    chatUids: string[],
  ): Promise<WhatsAppChatResponse[]> => {
    let res = await this._fetch({
      url: "/whatsapp/chats/batch-fetch",
      method: "POST",
      bodyMime: "application/json",
      body: { chat_uids: chatUids },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getAllUserDbChats = async (options?: {
    search?: string;
    includeArchived?: boolean;
    archivedOnly?: boolean;
    modifiedSince?: string;
  }): Promise<WhatsAppChatList> => {
    const params = new URLSearchParams();
    if (options?.search) {
      params.append("search", options.search);
    }
    if (options?.includeArchived) {
      params.append("include_archived", "true");
    }
    if (options?.archivedOnly) {
      params.append("archived_only", "true");
    }
    if (options?.modifiedSince) {
      params.append("modified_since", options.modifiedSince);
    }
    const url = params.toString()
      ? `/whatsapp/chats/?${params.toString()}`
      : `/whatsapp/chats/`;

    let res = await this._fetch({
      url,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  fetchAvailableChats = async (
    instanceId: string,
  ): Promise<WhatsAppChatResponse[]> => {
    let res = await this._fetch({
      url: `/whatsapp/instances/${instanceId}/fetch-chats`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  importChats = async (
    instanceId: string,
    chats: WhatsAppChatResponse[],
  ): Promise<WhatsAppChatResponse[]> => {
    let res = await this._fetch({
      url: `/whatsapp/instances/${instanceId}/import-chats`,
      method: "POST",
      body: chats,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getChatMessages = async (
    chatId: UUID,
    pageOptions: PageOptions,
  ): Promise<WhatsAppMessageList> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatId}/messages`,
      method: "GET",
      params: pageOptions,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getInstanceDbChats = async (
    instanceId: string,
    pageOptions?: PageOptions,
  ): Promise<WhatsAppChatResponse[]> => {
    let res = await this._fetch({
      url: `/whatsapp/instances/${instanceId}/chats`,
      method: "GET",
      params: pageOptions,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  sendMessage = async (
    chatId: UUID,
    text: string,
  ): Promise<WhatsAppApiResponse> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatId}/send-message`,
      method: "POST",
      body: {
        text: text,
      },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  sendReaction = async (
    chatId: UUID,
    messageKeyId: string,
    emoji: string,
  ): Promise<WhatsAppApiResponse> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatId}/messages/${encodeURIComponent(
        messageKeyId,
      )}/react`,
      method: "POST",
      body: { emoji },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  updateChatPartner = async (
    chatId: UUID,
    partnerId: string | null,
  ): Promise<WhatsAppChatResponse> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatId}/partner`,
      method: "PATCH",
      body: { partner_id: partnerId },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  deleteChat = async (chatId: UUID): Promise<void> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    // For 204 No Content, there might not be a JSON body, or it might be empty.
    // Check status code directly.
    if (res.ok) {
      // res.ok is true if status is 200-299
      if (res.status === 204) return; // Successfully deleted, no content
      // If other 2xx status codes might return JSON, handle here, but typically DELETE 204 doesn't.
      try {
        const jsonData = await res.json(); // Attempt to parse if there's a body for other 2xx cases
        return jsonData; // Or handle as appropriate
      } catch (e) {
        // If res.ok but no body or non-JSON body for a 2xx other than 204.
        return; // Consider this success for non-204 ok if no body expected.
      }
    }
    // If not res.ok, it's an error. Try to parse error from JSON.
    let errorPayload;
    try {
      errorPayload = await res.json();
    } catch (e) {
      // If error payload is not JSON
      throw new Error(
        `Failed to delete chat. Status: ${res.status} ${res.statusText}`,
      );
    }
    throw {
      message: `Failed to delete chat: ${errorPayload?.detail || res.statusText}`,
      status: res.status,
      payload: errorPayload,
    };
  };

  /**
   * Batch delete multiple WhatsApp chats
   */
  batchDeleteChats = async (
    chatUids: string[],
  ): Promise<{
    deleted_count: number;
    deleted: string[];
    errors: Array<{ chat_uid: string; error: string }>;
  }> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/batch-delete`,
      method: "POST",
      body: { chat_uids: chatUids },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  simulateMessage = async (
    chatJid: string,
    content: any,
    fromJid: string,
    fromName?: string,
    fromNumber?: string,
  ): Promise<WhatsAppMessageResponse> => {
    let body: any = {
      content: content,
      from_jid: fromJid,
      from_name: fromName,
      from_number: fromNumber,
    };
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatJid}/simulate-message`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getMedia = async (filename: string): Promise<Blob> => {
    let res = await this._fetch({
      url: `/whatsapp/media/${filename}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.blob();
  };

  getChatsWithUnread = async (): Promise<WhatsAppChatResponse[]> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/unread`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  markChatAsRead = async (chatId: UUID): Promise<WhatsAppChatResponse> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatId}/mark-read`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Mark all WhatsApp chats as read for the current user
   */
  markAllChatsAsRead = async (): Promise<{
    marked_count: number;
    message: string;
  }> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/mark-all-read`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Archive a WhatsApp chat (soft delete)
   */
  archiveChat = async (chatId: UUID): Promise<WhatsAppChatResponse> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatId}/archive`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Unarchive a WhatsApp chat
   */
  unarchiveChat = async (chatId: UUID): Promise<WhatsAppChatResponse> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatId}/unarchive`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Archive multiple WhatsApp chats in batch
   */
  archiveChatsBatch = async (
    chatUids: string[],
  ): Promise<{
    archived_count: number;
    archived: string[];
    errors: Array<{ chat_uid: string; error: string }>;
  }> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/archive-batch`,
      method: "POST",
      body: { chat_uids: chatUids },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  linkPartnerToChat = async (
    chatId: UUID,
    partnerId: UUID,
  ): Promise<WhatsAppChatResponse> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatId}/link-partner`,
      method: "POST",
      body: { partner_id: partnerId },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  unlinkPartnerFromChat = async (
    chatId: UUID,
  ): Promise<WhatsAppChatResponse> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatId}/unlink-partner`,
      method: "POST",
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postChatsUidTriggerWorkflow = async (
    chatId: UUID,
    body?: { workflow_id?: string; instructions?: string },
  ): Promise<any> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatId}/trigger-workflow`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
      body: body,
      bodyMime: body ? "application/json" : undefined,
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  patchChatsUidAiContext = async (
    chatId: UUID,
    body: { ai_context: string | null },
  ): Promise<any> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatId}/ai-context`,
      method: "PATCH",
      auth: ["OAuth2PasswordBearer"],
      body: body,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  patchChatsUidName = async (
    chatId: UUID,
    body: { name: string },
  ): Promise<WhatsAppChatResponse> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatId}/name`,
      method: "PATCH",
      auth: ["OAuth2PasswordBearer"],
      body: body,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get all chats with shares for management interface
   */
  getChatsWithShares = async (
    instanceId: string,
  ): Promise<WhatsAppChatManagementWithShares[]> => {
    let res = await this._fetch({
      url: `/whatsapp/instances/${instanceId}/chats-with-shares`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Update a chat from management interface (unified endpoint)
   */
  updateManagementChat = async (
    chatUid: string,
    data: WhatsAppChatManagementUpdate,
  ): Promise<WhatsAppChatManagement> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatUid}`,
      method: "PATCH",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Enable a WhatsApp chat (show in main chat list)
   */
  enableChat = async (chatUid: string): Promise<WhatsAppChatResponse> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatUid}/enable`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Disable a WhatsApp chat (hide from main chat list but keep storing messages)
   */
  disableChat = async (chatUid: string): Promise<WhatsAppChatResponse> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatUid}/disable`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Share or unshare a WhatsApp chat with a group
   */
  shareChatWithGroup = async (
    chatUid: string,
    groupId: string | null,
  ): Promise<WhatsAppChatResponse> => {
    let res = await this._fetch({
      url: `/whatsapp/chats/${chatUid}/share-group`,
      method: "POST",
      body: { group_id: groupId },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
